import { useEffect, useMemo } from 'react';
import { addDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ProductTypeName, SortOrder } from '@electricjs/core_entity-client';
import {
  Text,
  Button,
  Flex,
  spaces,
  SelectField,
  Country,
} from '@electricjs/arc';

import { MIN_INTERNATIONAL_ORDER_DAYS } from '@/constants/storefront';
import { useGetPaginatedProductsQuery } from '@/redux/slices/productApiSlice';
import { DateField } from 'common/DateField';

import { InternationalProductSelectOption } from './InternationalProductSelectOption';
import FetchingErrorBanner from '@/components/FetchingErrorBanner';
import ABMStorefront from '@/components/ABMManagement/ABMStorefront';
import useIsABMStatusUnknown from '@/hooks/storefront/useIsABMStatusUnknown';
import { useStorefront } from '@/components/Storefront/StorefrontProvider';

type SecondStepTileProps = {
  isSubmitting?: boolean;
  onRequest: () => void;
};

export const SecondStepTile = ({
  isSubmitting = false,
  onRequest,
}: SecondStepTileProps) => {
  const { t } = useTranslation('storefront');

  const isAbmStatusUnknown = useIsABMStatusUnknown();

  const {
    otherCountryFlowFirstStepForm,
    otherCountryFlowSecondStepForm,
    setOtherCountryFlowStep,
  } = useStorefront();

  const {
    data: paginatedProducts,
    isFetching: isFetchingProducts,
    error: productsError,
  } = useGetPaginatedProductsQuery({
    offset: 0,
    limit: 1000, // get all products in a single page
    orderBy: 'name',
    sortOrder: SortOrder.Asc,
    productTypes: [ProductTypeName.Laptop],
    countryCode: otherCountryFlowFirstStepForm?.getValues(
      'address.country'
    ) as Country,
  });

  const products = useMemo(
    () => paginatedProducts?.paginatedResponse.results || [],
    [paginatedProducts]
  );

  if (!otherCountryFlowSecondStepForm) {
    throw new Error();
  }
  const { handleSubmit, control, getValues, setValue, watch } =
    otherCountryFlowSecondStepForm;

  // make sure the selected hardware is updated according to the new list of options (to avoid old price)
  useEffect(() => {
    const selectedHardwareId = getValues('selectedHardware')?.id;

    if (selectedHardwareId) {
      const foundProduct = products.find(
        product => product.id === selectedHardwareId
      );

      setValue('selectedHardware', foundProduct);
    }
  }, [products, getValues, setValue]);

  const minDeliveryDate = useMemo(
    () => addDays(new Date(), MIN_INTERNATIONAL_ORDER_DAYS),
    []
  );

  const onSubmit = () => {
    // just tell the parent component (the form data is already available to it)
    onRequest();
  };

  const manufacturerName = watch('selectedHardware')?.manufacturerName;
  const isAppleDevice = manufacturerName?.toLowerCase() === 'apple';

  if (productsError) {
    return <FetchingErrorBanner resourceName="products" />;
  }

  return (
    <Flex vertical width="100%">
      <Text variant="heading-3" mb={spaces.space_l}>
        {t('international.secondStep.title')}
      </Text>
      <SelectField
        id="hardware-select"
        name="selectedHardware"
        key={getValues('selectedHardware')?.id}
        control={control}
        label={t('international.secondStep.labels.selectHardware')}
        required
        options={products}
        isLoading={isFetchingProducts}
        getOptionLabel={product => product.name}
        getOptionValue={product => product.id}
        customComponents={{
          Option: option => (
            <InternationalProductSelectOption
              data={option.data}
              isSelected={option.isSelected}
              isDisabled={option.isDisabled}
            />
          ),
        }}
        isSearchable
        isClearable
        searchableFields={['name', 'manufacturerName']}
        helperText={
          isAppleDevice
            ? t('international.secondStep.helperText.appleReminder')
            : undefined
        }
      />

      {/* Global ABM status (when the selected device is Apple) */}
      {isAppleDevice && (
        <ABMStorefront
          storeCountry={otherCountryFlowFirstStepForm?.getValues(
            'address.country'
          )}
        />
      )}

      <Text variant="heading-3" mt={spaces.space_2xl} mb={spaces.space_l}>
        {t('international.secondStep.deliveryTitle')}
      </Text>
      <DateField
        id="delivery-date-picker"
        name="targetDeliveryDate"
        control={control}
        label={t('international.secondStep.labels.targetDeliveryDate')}
        required
        helperText={t('international.secondStep.helperText.targetDeliveryDate')}
        minDate={minDeliveryDate}
      />

      <Flex width="100%" justifyContent="flex-end" columnGap={spaces.space_m}>
        <Button
          id="back-button"
          type="button"
          variant="text"
          iconBefore="arrow-left"
          onClick={() => setOtherCountryFlowStep(1)}>
          {t('international.secondStep.buttons.back')}
        </Button>
        <Button
          id="request-button"
          type="button"
          loading={isSubmitting}
          disabled={isAbmStatusUnknown && isAppleDevice}
          onClick={handleSubmit(onSubmit)}>
          {t('international.secondStep.buttons.request')}
        </Button>
      </Flex>
    </Flex>
  );
};
